import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import {
  Lifebuoy, Plus, CaretRight, MagnifyingGlass,
  CheckCircle, XCircle, PaperPlaneTilt, Buildings
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { BASE as API } from '../config/api';

const STATUS_MAP = {
  aperto: { labelKey: 'tickets.statusMap.aperto', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  in_lavorazione: { labelKey: 'tickets.statusMap.in_lavorazione', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  risolto: { labelKey: 'tickets.statusMap.risolto', color: 'bg-green-100 text-green-700 border-green-200' },
  chiuso: { labelKey: 'tickets.statusMap.chiuso', color: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const PRIORITY_MAP = {
  bassa: { labelKey: 'tickets.priorityMap.bassa', color: 'bg-gray-100 text-gray-600' },
  media: { labelKey: 'tickets.priorityMap.media', color: 'bg-blue-100 text-blue-700' },
  alta: { labelKey: 'tickets.priorityMap.alta', color: 'bg-orange-100 text-orange-700' },
  urgente: { labelKey: 'tickets.priorityMap.urgente', color: 'bg-red-100 text-red-700' },
};

const CATEGORY_KEYS = {
  bug: 'tickets.categoryMap.bug',
  come_fare: 'tickets.categoryMap.come_fare',
  funzionalita: 'tickets.categoryMap.funzionalita',
  account: 'tickets.categoryMap.account',
  preventivi: 'tickets.categoryMap.preventivi',
  pagamenti: 'tickets.categoryMap.pagamenti',
  altro: 'tickets.categoryMap.altro',
};

export default function TicketsPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [staffView, setStaffView] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const isStaff = !!user?.is_platform_admin;

  const [form, setForm] = useState({
    subject: '',
    description: '',
    priority: 'media',
    category: 'altro',
  });

  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (staffView) params.set('scope', 'all');
      const res = await fetch(`${API}/api/tickets?${params}`, { credentials: 'include' });
      const ticketsData = await res.json(); setTickets(Array.isArray(ticketsData) ? ticketsData : []);
    } catch (err) {
      console.error(err);
    }
  }, [filterStatus, staffView]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const createTicket = async () => {
    if (!form.subject) {
      toast.error(t('tickets.enterSubject'));
      return;
    }
    try {
      const res = await fetch(`${API}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...form,
          client_name: user?.name || user?.email || 'Utente',
          client_id: null,
          quote_id: null,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(t('tickets.ticketCreated'));
      setIsCreateOpen(false);
      setForm({ subject: '', description: '', priority: 'media', category: 'altro' });
      fetchTickets();
    } catch (err) {
      toast.error(t('tickets.createError'));
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      const res = await fetch(`${API}/api/tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(t('tickets.statusUpdated'));
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status }));
      }
    } catch (err) {
      toast.error(t('tickets.updateError'));
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selectedTicket) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/tickets/${selectedTicket.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: reply.trim() }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setSelectedTicket(updated);
      setReply('');
      toast.success(isStaff && updated.user_id !== user?.id ? 'Risposta inviata al cliente' : 'Messaggio inviato al supporto Infismart');
      fetchTickets();
    } catch (err) {
      toast.error('Invio non riuscito, riprova');
    } finally {
      setSending(false);
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm(t('tickets.deleteTicketConfirm'))) return;
    try {
      await fetch(`${API}/api/tickets/${ticketId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      toast.success(t('tickets.ticketDeleted'));
      setSelectedTicket(null);
      fetchTickets();
    } catch (err) {
      toast.error(t('tickets.deleteError'));
    }
  };

  const filteredTickets = (tickets || []).filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return t.subject?.toLowerCase().includes(s) || t.description?.toLowerCase().includes(s);
  });

  const statusCounts = {
    all: (tickets || []).length,
    aperto: (tickets || []).filter(t => t.status === 'aperto').length,
    in_lavorazione: (tickets || []).filter(t => t.status === 'in_lavorazione').length,
    risolto: (tickets || []).filter(t => t.status === 'risolto').length,
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="tickets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-[#0A0F1A] flex items-center gap-2">
            <Lifebuoy size={28} weight="duotone" className="text-[#0033A0]" />
            {t('tickets.supportTitle')}
          </h1>
          <p className="text-sm text-[#4A5568] mt-1">
            Scrivi al team Infismart: riceviamo subito la richiesta e ti rispondiamo qui e per email.
          </p>
        </div>
        <Button
          onClick={() => setIsCreateOpen(true)}
          className="bg-[#0033A0] hover:bg-[#002080] text-white"
          data-testid="create-ticket-btn"
        >
          <Plus size={16} className="mr-1" />
          {t('tickets.askHelp')}
        </Button>
      </div>

      {isStaff && (
        <div className="flex items-center gap-2 text-sm" data-testid="tickets-staff-toggle">
          <span className="text-[#4A5568]">Vista:</span>
          {[[false, 'I miei ticket'], [true, 'Tutti i ticket (staff)']].map(([v, label]) => (
            <button
              key={label}
              onClick={() => { setStaffView(v); setSelectedTicket(null); }}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium border ${staffView === v ? 'bg-[#0A0F1A] text-white border-[#0A0F1A]' : 'bg-white text-[#4A5568] border-[#E2E8F0]'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {['all', 'aperto', 'in_lavorazione', 'risolto'].map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors border ${
              filterStatus === status
                ? 'bg-[#0033A0] text-white border-[#0033A0]'
                : 'bg-white text-[#4A5568] border-[#E2E8F0] hover:bg-[#F4F6F8]'
            }`}
            data-testid={`filter-${status}`}
          >
            {status === 'all' ? t('common.all') : t(STATUS_MAP[status]?.labelKey)}
            <span className="ml-1.5 opacity-70">({statusCounts[status] || 0})</span>
          </button>
        ))}
        <div className="ml-auto">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#4A5568]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('tickets.searchTickets')}
              className="pl-8 h-8 w-48 text-sm"
              data-testid="search-tickets"
            />
          </div>
        </div>
      </div>

      {/* Ticket List */}
      <div className="space-y-2">
        {filteredTickets.length === 0 ? (
          <Card className="border-[#E2E8F0]">
            <CardContent className="py-16 text-center text-[#4A5568]">
              <Lifebuoy size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">{t('tickets.noTickets')}</p>
              <p className="text-xs mt-1">{t('tickets.noTicketDesc')}</p>
            </CardContent>
          </Card>
        ) : (
          filteredTickets.map(ticket => (
            <Card
              key={ticket.id}
              className={`border-[#E2E8F0] cursor-pointer hover:border-[#0033A0]/30 transition-colors ${
                selectedTicket?.id === ticket.id ? 'ring-1 ring-[#0033A0]/30' : ''
              }`}
              onClick={() => setSelectedTicket(ticket)}
              data-testid={`ticket-${ticket.id}`}
            >
              <CardContent className="p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-medium text-[#0A0F1A] truncate">{ticket.subject}</h3>
                      <Badge className={`text-[10px] shrink-0 ${STATUS_MAP[ticket.status]?.color || ''}`}>
                        {t(STATUS_MAP[ticket.status]?.labelKey) || ticket.status}
                      </Badge>
                      <Badge className={`text-[10px] shrink-0 ${PRIORITY_MAP[ticket.priority]?.color || ''}`}>
                        {t(PRIORITY_MAP[ticket.priority]?.labelKey) || ticket.priority}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#4A5568]">
                      {staffView && (
                        <span className="flex items-center gap-1 font-medium text-[#0A0F1A]">
                          <Buildings size={12} />{ticket.company_name || ticket.client_name || ticket.user_email}
                        </span>
                      )}
                      <span>{t(CATEGORY_KEYS[ticket.category]) || ticket.category}</span>
                      {(ticket.messages || []).length > 0 && (
                        <span>{ticket.messages.length} {ticket.messages.length === 1 ? 'risposta' : 'risposte'}</span>
                      )}
                      <span>{new Date(ticket.created_at).toLocaleDateString('it-IT')}</span>
                    </div>
                    {ticket.description && (
                      <p className="text-xs text-[#4A5568] mt-1 line-clamp-1">{ticket.description}</p>
                    )}
                  </div>
                  <CaretRight size={16} className="text-[#4A5568] mt-1 shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Ticket Detail */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-outfit pr-6">{selectedTicket.subject}</DialogTitle>
                <DialogDescription>
                  {t(CATEGORY_KEYS[selectedTicket.category])} - {t('tickets.openedOn')} {new Date(selectedTicket.created_at).toLocaleString('it-IT')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={STATUS_MAP[selectedTicket.status]?.color || ''}>
                    {t(STATUS_MAP[selectedTicket.status]?.labelKey)}
                  </Badge>
                  <Badge className={PRIORITY_MAP[selectedTicket.priority]?.color || ''}>
                    {t(PRIORITY_MAP[selectedTicket.priority]?.labelKey)}
                  </Badge>
                </div>

                {/* Conversation */}
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="ticket-conversation">
                  {selectedTicket.description && (
                    <div className="p-3 bg-[#F4F6F8] rounded-sm text-sm">
                      <p className="text-[11px] text-[#4A5568] mb-1">
                        {selectedTicket.company_name || selectedTicket.client_name} · {new Date(selectedTicket.created_at).toLocaleString('it-IT')}
                      </p>
                      <p className="text-[#0A0F1A] whitespace-pre-wrap">{selectedTicket.description}</p>
                    </div>
                  )}
                  {(selectedTicket.messages || []).map((m, i) => (
                    <div
                      key={i}
                      className={`p-3 rounded-sm text-sm ${m.author === 'staff' ? 'bg-[#0033A0]/5 border border-[#0033A0]/20 ml-6' : 'bg-[#F4F6F8] mr-6'}`}
                      data-testid={`ticket-message-${m.author}`}
                    >
                      <p className="text-[11px] text-[#4A5568] mb-1">
                        <span className="font-medium text-[#0A0F1A]">{m.author_name}</span> · {new Date(m.at).toLocaleString('it-IT')}
                      </p>
                      <p className="text-[#0A0F1A] whitespace-pre-wrap">{m.text}</p>
                    </div>
                  ))}
                  {(selectedTicket.messages || []).length === 0 && (
                    <p className="text-xs text-[#4A5568]">Il team Infismart ha ricevuto la richiesta: la risposta comparirà qui e ti arriverà anche per email.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={3}
                    placeholder={isStaff && selectedTicket.user_id !== user?.id ? 'Rispondi al cliente…' : 'Aggiungi un messaggio per il supporto…'}
                    className="text-sm"
                    data-testid="ticket-reply-input"
                  />
                  <div className="flex justify-end">
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} size="sm" className="bg-[#0033A0] hover:bg-[#002080] text-white" data-testid="ticket-reply-send">
                      <PaperPlaneTilt size={14} className="mr-1" />{sending ? 'Invio…' : 'Invia'}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs mb-2 block">{t('tickets.changeStatus')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_MAP).filter(([key]) => (isStaff && selectedTicket.user_id !== user?.id) || key !== 'in_lavorazione').map(([key, val]) => (
                      <Button
                        key={key}
                        variant={selectedTicket.status === key ? 'default' : 'outline'}
                        size="sm"
                        className={`text-xs h-7 ${selectedTicket.status === key ? 'bg-[#0033A0]' : ''}`}
                        onClick={() => updateTicketStatus(selectedTicket.id, key)}
                        data-testid={`set-status-${key}`}
                      >
                        {t(val.labelKey)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-2 border-t border-[#E2E8F0]">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => deleteTicket(selectedTicket.id)}
                    data-testid="delete-ticket-btn"
                  >
                    <XCircle size={14} className="mr-1" /> {t('tickets.deleteLabel')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Ticket Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('tickets.askHelp')}</DialogTitle>
            <DialogDescription>
              {t('tickets.supportDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">{t('tickets.subjectRequired')}</Label>
              <Input
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder={t('tickets.subjectPlaceholder')}
                className="mt-1"
                data-testid="ticket-subject"
              />
            </div>

            <div>
              <Label className="text-xs">{t('tickets.descriptionLabel')}</Label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder={t('tickets.descriptionPlaceholder')}
                className="mt-1 w-full h-28 px-3 py-2 text-sm border border-[#E2E8F0] rounded-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#0033A0]"
                data-testid="ticket-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('tickets.categoryLabel')}</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="mt-1" data-testid="ticket-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_KEYS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{t(v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('tickets.priority')}</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger className="mt-1" data-testid="ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_MAP).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{t(v.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>{t('common.cancel')}</Button>
              <Button
                onClick={createTicket}
                className="bg-[#0033A0] hover:bg-[#002080] text-white"
                data-testid="submit-ticket-btn"
              >
                {t('tickets.submitTicket')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

